/**
 * The browser tab's session: where it is stored, the only synchronous access to it, and the rule
 * that decides, when a document loads, whether the id it finds is this tab's at all (D-136).
 *
 * This module deliberately imports NOTHING. `ApiFactory` reads the value on every request and
 * `sse-client.ts` imports `ApiFactory`, so anything here that reached back into the api layer would
 * close an import cycle. Issuing a session needs the api layer, so that half lives in
 * `services/tab-session.ts` and this half stays dependency-free.
 *
 * THE ARRIVAL RULE RUNS WHEN THIS MODULE IS EVALUATED, at the bottom of the file, and that placement
 * is the guarantee rather than a convenience. Every reader of the id imports this module, and a
 * module is evaluated before anything that imports it, so no reader can run before the rule has.
 * A call from `main.ts` could not promise that: the production build puts this module in a shared
 * chunk that the entry chunk imports, and an imported chunk is evaluated before the entry's own
 * body, so code in it could read the id before a call written in `main.ts` ever ran (read in the
 * built entry chunk, 2026-09-26).
 *
 * Duplicating the key string is the thing being avoided rather than a style preference. A storage
 * key written as a literal in two places type-checks in both and agrees in neither once one of them
 * moves - the renamed account key in #365 did exactly that to the Playwright auth snapshot, which
 * kept compiling and would have signed every persona out of every restored context.
 */

/**
 * The per-tab session id, as issued by the SERVER.
 *
 * <p>The client does NOT mint this. It is a `Users.UserTabSession` row created by the server at
 * login, at account switch, or on request at `POST /api/TabSession/issue`, and the client's only
 * job is to remember which one it was given. A value the caller chooses cannot be the thing lock
 * ownership is judged by, which is why the old client-minted GUID had to go (D-114, #366).</p>
 *
 * <p>Named for the TAB, not for SSE. The event stream is one CONSUMER of this value - it puts it in
 * the connect URL because EventSource cannot set headers - but it neither owns it nor creates it.
 * The previous name said `sse`, which is what made it look like the stream's property.</p>
 *
 * <p>In `sessionStorage`, so it is per tab and survives a reload of that tab. It is NOT safe from
 * inheritance by storage alone: Chromium copies a tab's sessionStorage into a tab made by Duplicate
 * Tab and into a closed tab brought back by Reopen Closed Tab, so two live tabs could arrive holding
 * one id, and either one's unload release (D-116) would free the other's locks. What keeps it one
 * tab's is the arrival rule below (D-136): only a reload keeps the id it finds, and only when no
 * other live tab holds it; every other arrival discards it before anything reads it.
 * `agwa-active-account` lives alongside it, is derived from the user id, and IS kept by a copied tab
 * on purpose, which is what makes this a session and that an account key.</p>
 *
 * <p>It is an IDENTIFIER and never a credential (D-114). Holding it authorises nothing, because
 * every use is proved against the authenticated caller and authentication is the httpOnly cookie
 * that JavaScript cannot read. That is why it is safe here, in a response body, and broadcast over
 * SSE to everyone watching a lock.</p>
 */
export const TAB_SESSION_ID_STORAGE_KEY = 'agwa-tab-session-id';

/**
 * The keep-or-discard decision on the id this document found at load, while it is still open.
 *
 * <p>Set by the arrival rule for the one case that cannot be decided synchronously - a reload,
 * which must ask the Web Locks API whether another live tab holds the id - and null at all other
 * times. While it is set, `readTabSessionId` answers "no session", so no request, stream or lock
 * comparison can quote an id this tab may be about to give up; `tabSessionArrival()` is what an
 * asynchronous caller awaits before it looks.</p>
 */
let arrivalPending: Promise<void> | null = null;

/**
 * The session this tab was issued, or undefined if it has not been issued one yet.
 *
 * <p>Also undefined while the arrival decision is open (above): an id that may yet be discarded is
 * not this tab's to quote.</p>
 */
export function readTabSessionId(): string | undefined {
  if (arrivalPending) return undefined;
  return readStoredTabSessionId();
}

/** The raw stored value, bypassing the arrival gate: for the arrival rule and the claim only. */
function readStoredTabSessionId(): string | undefined {
  return sessionStorage.getItem(TAB_SESSION_ID_STORAGE_KEY) ?? undefined;
}

/** Resolves once the arrival decision has settled; at once when there is none open. */
export function tabSessionArrival(): Promise<void> {
  return arrivalPending ?? Promise.resolve();
}

/** Remembers a session the SERVER issued. Never called with a locally invented value. */
export function writeTabSessionId(tabSessionId: string): void {
  sessionStorage.setItem(TAB_SESSION_ID_STORAGE_KEY, tabSessionId);
  // Every id this tab uses is claimed for its whole life, so a later reload of ANOTHER tab that
  // somehow arrived with the same id finds it held and discards it (D-136). A freshly issued id
  // cannot be held elsewhere, so the outcome is not awaited.
  void claimTabSession(tabSessionId);
}

/**
 * Adopts the session carried on a login or switch response.
 *
 * Ignores a blank or absent value rather than storing one. A failed login returns no session, and
 * writing an empty string would leave the tab believing it has one, so it would never ask for a real
 * one and would silently hold no locks for the rest of its life.
 */
export function adoptIssuedTabSession(
  tabSessionId: string | undefined | null
): void {
  if (tabSessionId) {
    writeTabSessionId(tabSessionId);
  }
}

/**
 * Forgets this tab's session, after the server has ended it.
 *
 * Clearing without the server having ended it would strand the row: still live, still holding
 * whatever locks it had, and now unreachable by the only tab that could release them. The arrival
 * rule's discard is the one exception, and says why at `discardArrivedTabSession`.
 */
export function clearTabSessionId(): void {
  sessionStorage.removeItem(TAB_SESSION_ID_STORAGE_KEY);
  releaseHeldClaim();
}

// ─────────────────────────────────────────────────────────────────────────────
// The claim: a Web Lock named by the id, held for the tab's life
// ─────────────────────────────────────────────────────────────────────────────

/** The Web Lock names are prefixed so they cannot collide with any other lock the origin takes. */
const TAB_SESSION_LOCK_PREFIX = 'agwa-tab-session:';

/** The claim this tab holds right now, and how to let go of it. */
let heldClaim: { tabSessionId: string; release: () => void } | null = null;

/**
 * The Locks API, or undefined where the browser (or the test environment) has none. Both absence
 * and a null property count: happy-dom declares `navigator.locks` and returns null from it.
 */
function browserLocks(): LockManager | undefined {
  return typeof navigator === 'undefined'
    ? undefined
    : (navigator.locks ?? undefined);
}

/**
 * Claims `tabSessionId` for this tab, without waiting, and holds it until the tab lets go of the id
 * or the document goes away.
 *
 * <p>The claim is an exclusive Web Lock named by the id, requested with `ifAvailable` and held by a
 * callback promise that never settles until `releaseHeldClaim` resolves it. The browser drops it
 * when the document is destroyed, which is what makes "is this id held" mean "is there a live tab
 * using it" (measured on Chromium 151.0.7922.34, 2026-09-26, through the browser's own
 * chrome.tabs.duplicate and a scripted page.reload: a reloaded document found the old document's
 * claim already gone in 200 of 200 reloads, while a duplicated tab found the original's held and a
 * 3-second wait for it timed out).</p>
 *
 * <p>Holding it does not cost the page the back/forward cache (measured the same day, with the cache
 * enabled): a page holding its claim was restored on Back with the claim still held. The one
 * interaction is eviction: if another page requests the same name while this one sits in the cache,
 * Chromium evicts it (WebLocksContention) and Back loads it afresh. Only a tab arriving with a copy
 * of this id would ask for it, and a copy cannot be made of a page that is in the cache, so the
 * pageshow reconcile in lock-store.ts still runs for a restored page as before. Playwright launches
 * Chromium with the cache disabled, so no journey exercises this; the measurement is the proof.</p>
 *
 * <p>Resolves true when the claim is held (or was already held by this tab), false when another
 * live tab holds it, the request failed, or storage no longer holds the id by the time it is
 * granted. Resolves true WITHOUT holding anything where there is no Locks API: the caller cannot
 * check, and D-136 settles that such a tab keeps its id on a reload.</p>
 */
function claimTabSession(
  tabSessionId: string,
  locks: LockManager | undefined = browserLocks()
): Promise<boolean> {
  if (!locks) return Promise.resolve(true);
  if (heldClaim?.tabSessionId === tabSessionId) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    locks
      .request(
        TAB_SESSION_LOCK_PREFIX + tabSessionId,
        { mode: 'exclusive', ifAvailable: true },
        (lock) => {
          if (!lock) {
            resolve(false);
            return undefined;
          }
          // Granted after the tab let go of this id (cleared, or replaced by a switch while the
          // request was in flight): holding it now would claim an id the tab no longer uses.
          if (readStoredTabSessionId() !== tabSessionId) {
            resolve(false);
            return undefined;
          }
          // One claim at a time: the id this tab used before is no longer its, so a tab that
          // arrives with THAT id may keep it.
          releaseHeldClaim();
          return new Promise<void>((release) => {
            heldClaim = { tabSessionId, release };
            resolve(true);
          });
        }
      )
      // A refused request (an opaque origin, a name the API rejects) cannot prove the id is free,
      // and keeping an id another tab uses is the costlier mistake, so it counts as held.
      .catch(() => resolve(false));
  });
}

/** Lets go of this tab's claim, if it holds one. */
function releaseHeldClaim(): void {
  const claim = heldClaim;
  heldClaim = null;
  claim?.release();
}

// ─────────────────────────────────────────────────────────────────────────────
// The arrival rule (D-136)
// ─────────────────────────────────────────────────────────────────────────────

/** What the arrival decided. `empty`: there was no id to decide about. */
export type TabSessionArrival = 'empty' | 'keep' | 'discard';

/** What the decision reads from the browser, injectable so it can be exercised without one. */
export interface TabSessionArrivalEnvironment {
  /** The navigation timing entry's `type`, or undefined where the browser gives none. */
  navigationType: string | undefined;
  /** The Locks API, or undefined where the browser has none. */
  locks: LockManager | undefined;
}

/** The environment the running page actually has. */
function browserArrivalEnvironment(): TabSessionArrivalEnvironment {
  const entry =
    typeof performance !== 'undefined' &&
    typeof performance.getEntriesByType === 'function'
      ? (performance.getEntriesByType('navigation')[0] as
          PerformanceNavigationTiming | undefined)
      : undefined;
  return { navigationType: entry?.type, locks: browserLocks() };
}

/**
 * Drops an id this document arrived with and is not keeping.
 *
 * <p>Deliberately NOT a request to the server to end the session, which is what `clearTabSessionId`
 * normally follows. The id belongs to another live tab (a duplicate's source, which is still using
 * it), or to a document that has gone (a closed tab, or this tab's previous page), whose unload
 * already released every lock the session held (D-116). Ending it here would take a live tab's locks;
 * leaving it strands only a row that holds nothing, which is left to the server's lifecycle.</p>
 */
function discardArrivedTabSession(): void {
  clearTabSessionId();
}

/**
 * Decides, and applies, keep-or-discard for the id this document found (D-136).
 *
 * <p>The architect's intent: "any tab opened at any time is a fresh session". A tab keeps the id it
 * finds only when BOTH hold: the load is a reload (the navigation timing entry says `reload`), and
 * no other live tab holds the id (this tab can take its claim). Every other arrival discards it: a
 * duplicate or a reopened tab (both arrive as `back_forward` with a copy of the source's storage,
 * measured on Chromium 151, 2026-09-26), and any full navigation into the app, including from
 * another page of the app in the same tab. Such a tab is issued a new session by the existing path,
 * `ensureTabSessionId`, at its first need.</p>
 *
 * <p>THE ACCOUNT KEYS ARE NOT TOUCHED. A tab that arrives carrying `agwa-active-account` is a copy of
 * another tab and keeps that tab's user, because the URL it opens is that user's page and the
 * last-focused user may not be permitted to see it; a tab that arrives with none takes the
 * last-focused user from localStorage, as `session-manager.ts` already does.</p>
 *
 * <p>Returns what it decided, settled once applied. Every arrival but a reload with a Locks API is
 * applied synchronously, before this returns. Runs once on its own when this module is evaluated
 * (below); exported so the rule can be exercised against an injected environment.</p>
 */
export function arriveTabSession(
  environment: TabSessionArrivalEnvironment = browserArrivalEnvironment()
): Promise<TabSessionArrival> {
  const found = readStoredTabSessionId();
  if (!found) return Promise.resolve('empty');

  // Not a reload: a copied tab, a reopened one, or a fresh navigation into the app. None of them is
  // the document that was issued this id, so it goes now, before any reader can see it.
  if (environment.navigationType !== 'reload') {
    discardArrivedTabSession();
    return Promise.resolve('discard');
  }

  // A reload where the browser cannot tell us whether another tab holds the id. D-136: keep on
  // reload only. Every browser the product supports has Web Locks, so this is the fallback for an
  // environment outside that list, and it errs toward the one tab that most likely owns the id.
  if (!environment.locks) return Promise.resolve('keep');

  // A reload: keep only if no other live tab holds the id. `ifAvailable`, not a wait: measured on
  // Chromium 151, the reloaded document's claim was already released when the new document asked
  // in 200 of 200 reloads (see claimTabSession), so a wait would only delay the discard of an id a
  // live twin holds. If a browser ever reports otherwise, @LOCK-022 is the journey that goes red.
  let decided: TabSessionArrival = 'keep';
  const decision = claimTabSession(found, environment.locks).then((free) => {
    if (free) return;
    decided = 'discard';
    // Only the id this decision was about. A sign-in completing during the check may already have
    // written a different one, and that one is this tab's.
    if (readStoredTabSessionId() === found) discardArrivedTabSession();
  });

  // Held shut until the answer, and opened whether it resolves or rejects: a failure to decide must
  // not leave the tab unable to read its session for the rest of its life.
  const gate: Promise<void> = decision
    .catch(() => undefined)
    .finally(() => {
      // Only this gate's own clearing: a later decision must not be opened by an earlier one.
      if (arrivalPending === gate) arrivalPending = null;
    });
  arrivalPending = gate;
  return gate.then(() => decided);
}

// Runs the rule for this document, once, as this module is evaluated. See the file header for why
// this line, and not a call from main.ts, is what guarantees no reader runs first. Not awaited: a
// discard has already happened when it returns, and a reload's check holds the gate shut itself.
// Skipped where there is no sessionStorage at all: the Playwright suite imports the key constant
// from this module into Node, where there is no tab to decide about.
if (typeof sessionStorage !== 'undefined') void arriveTabSession();
